
import { logger, logError, logInfo } from '../logger';
import type { MonitoringServiceAdapter } from './types';
import { BaseAdapter } from './adapters/baseAdapter';
import { SentryAdapter } from './adapters/sentryAdapter';
import { LogRocketAdapter } from './adapters/logRocketAdapter';

class MonitoringService {
  private static instance: MonitoringService;
  private adapters: Map<string, MonitoringServiceAdapter> = new Map();
  private isInitialized = false;
  private globalErrorsRegistered = false;
  
  private constructor() {}
  
  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }
  
  public registerAdapter(name: string, adapter: MonitoringServiceAdapter): void {
    this.adapters.set(name, adapter);
    logger.info(`Registered monitoring adapter: ${name}`, 'monitoring');
  }
  
  public async initialize(configs: Record<string, Record<string, any>> = {}): Promise<void> {
    logger.info('Initializing monitoring service', 'monitoring', { 
      adapterCount: this.adapters.size,
      configuredAdapters: Object.keys(configs)
    });
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        const config = configs[name] || {};
        await adapter.initialize(config);
        logger.info(`Initialized ${name} adapter`, 'monitoring');
      } catch (err) {
        logError(`Failed to initialize ${name} adapter`, 'monitoring', {
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }
    
    this.isInitialized = true;
    logger.info('Monitoring service initialization complete', 'monitoring');
    
    // Register global error handlers if not already registered
    this.registerGlobalErrorHandlers();
  }
  
  public captureError(error: Error, context?: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return;
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.captureError(error, context);
      } catch (err) {
        logError(`Error in ${name} adapter captureError`, 'monitoring', {
          originalError: error.message,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
  
  public captureMessage(message: string, level: string = 'info', context?: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return;
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.captureMessage(message, level, context);
      } catch (err) {
        logError(`Error in ${name} adapter captureMessage`, 'monitoring', {
          message,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
  
  public capturePerformance(name: string, duration: number, context?: Record<string, any>): (finalContext?: Record<string, any>) => void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return () => {};
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.capturePerformance(name, duration, context);
      } catch (err) {
        logError(`Error in ${name} adapter capturePerformance`, 'monitoring', {
          performanceName: name,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
    
    return (finalContext?: Record<string, any>) => {
      if (finalContext) {
        this.capturePerformance(name, duration, finalContext);
      }
    };
  }
  
  public setUser(userData: Record<string, any>): void {
    if (!this.isInitialized) {
      logger.warn('Monitoring service not initialized', 'monitoring');
      return;
    }
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        adapter.setUser(userData);
      } catch (err) {
        logError(`Error in ${name} adapter setUser`, 'monitoring', {
          userId: userData.id,
          adapterError: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
  
  private registerGlobalErrorHandlers(): void {
    if (this.globalErrorsRegistered || typeof window === 'undefined') {
      return;
    }
    
    window.addEventListener('error', (event) => {
      const error = event.error || new Error(`Script error: ${event.message}`);
      this.captureError(error, {
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        type: 'window.onerror'
      });
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      
      this.captureError(error, {
        type: 'unhandledrejection'
      });
    });
    
    this.globalErrorsRegistered = true;
    logger.info('Global error handlers registered', 'monitoring');
  }
  
  public async shutdown(): Promise<void> {
    logger.info('Shutting down monitoring service', 'monitoring');
    
    for (const [name, adapter] of this.adapters.entries()) {
      try {
        await adapter.shutdown();
        logger.info(`Shut down ${name} adapter`, 'monitoring');
      } catch (err) {
        logError(`Error shutting down ${name} adapter`, 'monitoring', {
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }
  }
}

// Export the singleton instance
export const monitoringService = MonitoringService.getInstance();

// Factory function for creating standard adapters
export const createAdapter = (type: 'sentry' | 'logrocket' | 'base'): MonitoringServiceAdapter => {
  switch (type) {
    case 'sentry':
      return new SentryAdapter();
    case 'logrocket': 
      return new LogRocketAdapter();
    default:
      return new BaseAdapter(type);
  }
};

// Helper function to start monitoring with common presets
export const initializeMonitoring = (
  services: Array<'sentry' | 'logrocket' | 'base'> = ['base'],
  configs: Record<string, Record<string, any>> = {}
): Promise<void> => {
  // Register each requested adapter
  services.forEach(service => {
    monitoringService.registerAdapter(service, createAdapter(service));
  });
  
  // Initialize the service with provided configs
  return monitoringService.initialize(configs);
};
